import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { ChatService, ChatTurn } from '../../services/chat.service';
import { CatalogService } from '../../services/catalog.service';
import { SiteSettings } from '../../models/catalog.model';

interface ChatMessage extends ChatTurn {
  error?: boolean;
}

@Component({
  selector: 'app-floating-actions',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './floating-actions.component.html',
  styleUrl: './floating-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FloatingActionsComponent implements AfterViewChecked {
  private chat = inject(ChatService);
  private catalog = inject(CatalogService);
  private router = inject(Router);

  @ViewChild('thread') private thread?: ElementRef<HTMLDivElement>;

  readonly settings = toSignal(this.catalog.settings$, { initialValue: {} as SiteSettings });

  readonly hidden = signal(false);
  readonly open = signal(false);
  readonly typing = signal(false);
  readonly draft = signal('');
  readonly messages = signal<ChatMessage[]>([
    {
      role: 'assistant',
      content:
        'Hello! I’m the HNH Jewellery assistant. Ask me about our pieces, collections, prices, payments or delivery. ✨',
    },
  ]);

  private shouldScroll = false;

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((e) => this.hidden.set(e.urlAfterRedirects.startsWith('/admin')));
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.thread) {
      this.thread.nativeElement.scrollTop = this.thread.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  get whatsappLink(): string {
    const number = this.settings()['whatsapp_number'] || '923463675594';
    return `https://wa.me/${number}`;
  }

  toggle(): void {
    this.open.update((v) => !v);
    this.shouldScroll = this.open();
  }

  send(): void {
    const text = this.draft().trim();
    if (!text || this.typing()) return;

    const history: ChatTurn[] = this.messages()
      .filter((m) => !m.error)
      .map(({ role, content }) => ({ role, content }));

    this.messages.update((list) => [...list, { role: 'user', content: text }]);
    this.draft.set('');
    this.typing.set(true);
    this.shouldScroll = true;

    this.chat.send(text, history).subscribe({
      next: ({ reply }) => {
        this.messages.update((list) => [...list, { role: 'assistant', content: reply }]);
        this.typing.set(false);
        this.shouldScroll = true;
      },
      error: () => {
        this.messages.update((list) => [
          ...list,
          {
            role: 'assistant',
            content:
              'I couldn’t reach our assistant just now — please try again, or message us on WhatsApp.',
            error: true,
          },
        ]);
        this.typing.set(false);
        this.shouldScroll = true;
      },
    });
  }
}
